import {
  QueryFunction,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
} from 'react-query'
import {useInfiniteQueryUntilEnd} from '../hooks/useInfiniteQueryUntilEnd'
import {http} from '../services/api/http'
import {fetchSessions as fetchSessionsEndpoint} from '../services/coreApi/sessions'
import {useRequiredAuth} from '../stores/AuthStore'

type Session = {
  ID: string
  clientID: string
  self: string
  name: string
  description: string
  isActive: boolean
}

type QueryFnData = {
  results: Session[]
  totalCount: number
  nextPage: string
}
type Client = ReturnType<typeof useRequiredAuth>['client'] // I think there is a better way with Generics, but don't have time to figure it out
type GetQueryOptions = (params: {
  client: Client
  season: number
}) => UseInfiniteQueryOptions<QueryFnData>

type QueryFn = (params: {
  client: Client
  season: number
}) => QueryFunction<QueryFnData>

const PAGE_SIZE = 100
const QUERY_STALE_TIME = 1000 * 60 * 30 // 30 minutes

const queryKeys = {
  all: () => [{resource: 'sessions'}] as const, // [{ resource: 'sessions'}]
  lists: () => [{...queryKeys.all()[0], type: 'list'}] as const, // [{ resource: 'sessions', type: 'list}]
  list: ({season}: {season: number}) =>
    [{...queryKeys.all()[0], type: 'list', season}] as const, // [{ resource: 'sessions', type: 'list, season: '2020'}]
}

const queryFn: QueryFn =
  ({client, season}) =>
  async ({pageParam = 1}) => {
    const response = await fetchSessionsEndpoint(http, {
      clientID: client.ID, // technically doesn't need to be apart of query key, but currently passing it for queryFn so kind of should be
      pageNumber: pageParam,
      pageSize: PAGE_SIZE,
      seasonID: season,
    })

    const {Results, TotalCount, Next} = response.data

    return {
      results: Results.map((result) => ({
        ID: result.ID.toString(),
        clientID: result.ClientID.toString(),
        self: result.Self,
        name: result.Name,
        description: result.Description,
        isActive: result.IsActive,
      })),
      totalCount: TotalCount,
      nextPage: Next,
    }
  }

const getQueryOptions: GetQueryOptions = ({client, season}) => ({
  queryKey: queryKeys.list({season}),
  queryFn: queryFn({client, season}),
  staleTime: QUERY_STALE_TIME,
  getNextPageParam: (lastPage, allPages) =>
    // If the last page has some real next URL string, then set the pageParam to the next page
    lastPage.nextPage !== '' ? allPages.length + 1 : undefined,
})

type UseSessions = (params: {
  season: number
}) => UseInfiniteQueryResult<QueryFnData>

export const useSessions: UseSessions = ({season}) => {
  const {client} = useRequiredAuth()

  return useInfiniteQueryUntilEnd<QueryFnData>(
    getQueryOptions({client, season}),
  )
}
